import { watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { logoutReason } from '@/composables/useAuth'
import { usePublicToken } from '@/composables/usePublicToken'
import { setDatadogUserAndAccount } from '@/plugins/datadog'
import { clearSurvicateUser } from '@/plugins/survicate'
import { queryClient, idbQueryStorage } from '@/plugins/vue-query'
import useAuthStore from '@/stores/authStore'
import { useDialogsStore } from '@/stores/dialogsStore'
import { store as legacyStoreUntyped } from '@old/store'

const legacyStore = legacyStoreUntyped as {
  dispatch(action: 'set_state_property', payload: { property: string; data: unknown }): Promise<unknown>
}

// LEGACY: the implicit logout path (idle timeout / refresh failure) bypasses
// the old user_mixin.logout_user, which owned this reset — without it the
// previous user's documents stay in memory on the login screen (shared-device
// privacy). Field list mirrors logout_user's base_data minus the dialog keys
// above; dies with the legacy views.
const LEGACY_LOGOUT_STATE: Record<string, unknown> = {
  pre_login_user_tokens: null,
  public_token: null,
  user: {},
  unread_tickets: 0,
  dont_show_profile_dialog: false,
  direct_debit_payments: {},
}

function resetLegacyProperties(state: Record<string, unknown>) {
  for (const [property, data] of Object.entries(state)) {
    // Clone per dispatch — legacy components mutate their state objects in
    // place, and a shared module constant handed to the store would get
    // polluted, turning a later "reset" into a dialog-opening write.
    void legacyStore.dispatch('set_state_property', { property, data: structuredClone(data) })
  }
}

function resetLegacyState() {
  resetLegacyProperties(LEGACY_LOGOUT_STATE)
  sessionStorage.removeItem('dont_show_profile_dialog')
}

// PERMANENT composable: keeps the TanStack query cache in sync with auth
// transitions. App-agnostic core ported from ada-app-ui — token-type
// classification, /user--extended-data handling, creditor onboarding, guard
// re-validation and return-to are intentionally not ported.
export function useAuthEffects() {
  const { clearPublicToken } = usePublicToken()
  const route = useRoute()
  const router = useRouter()
  const authStore = useAuthStore()
  const dialogsStore = useDialogsStore()

  // 1) Implicit logout (idle timeout / refresh failure) → flush cache + redirect
  watch(
    () => authStore.selectedToken == null,
    async (loggedOut) => {
      if (!loggedOut || route.meta.public === true) return
      // Clear analytics identity — RUM/NPS must not keep attributing the
      // logged-out session to the previous user (shared-device privacy).
      setDatadogUserAndAccount(null, null)
      clearSurvicateUser()
      // LEGACY: drop the previous user's data (see resetLegacyState). Runs
      // twice on explicit logout (logout_user already resets) — idempotent,
      // harmless.
      resetLegacyState()
      await queryClient.cancelQueries()
      await idbQueryStorage.clear()
      queryClient.clear()

      // The reason stays set until the login view has shown it: a second redirect during the
      // same logout (legacy bootstrap, tab sync) must not land on a bare /authenticate.
      const reason = logoutReason.value
      void router.push(reason != null ? { path: '/authenticate', query: { reason } } : '/authenticate')
    }
  )

  // 1b) Close the global dialogs on EVERY auth transition — both directions. A
  //     login (e.g. synced from another tab) does not pass through the logout
  //     watcher above, but a dialog opened while logged out (pay-with-OCR on the
  //     login screen) must not survive the identity change (shared-device
  //     privacy). Trade-off: an OCR deep-link stash is dropped if the user logs
  //     in instead of paying anonymously — acceptable, the flow itself is intact.
  watch(
    () => authStore.selectedToken != null,
    () => {
      dialogsStore.reset()
      // The public token is a credential minted for the logged-out payer — it must
      // not outlive that identity either (ADA-4432).
      clearPublicToken()
    }
  )

  // 2) User switch (token_id change) → reset the query cache. NO navigation
  //    here: the push('/') is owned by useLegacyBootstrap's identity watcher
  //    until views are migrated (avoids double navigation).
  watch(
    () => authStore.selectedToken?.payload?.token_id ?? null,
    async (id, oldId) => {
      if (oldId == null || id == null || id === oldId) return
      // Watcher 1b only sees null-transitions — an A→B switch keeps the token non-null, but a
      // dialog opened under the previous identity must not survive it (shared-device privacy).
      dialogsStore.reset()
      clearPublicToken()
      await queryClient.cancelQueries()
      await idbQueryStorage.clear()
      void queryClient.resetQueries()
    }
  )
}
